import React, { useEffect, useRef, useState } from 'react';
import { Modal, Tag, Tooltip, Empty, Button, Spin, Alert } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../../config';
import './OzonPriceDetailsModal.css';

const formatMoney = (value, currency = 'RUB') => {
    const number = Number(value) || 0;
    const symbol = currency === 'RUB' ? '₽' : currency;
    return `${number.toLocaleString('ru-RU', {
        minimumFractionDigits: number % 1 ? 2 : 0,
        maximumFractionDigits: 2,
    })} ${symbol}`;
};

const formatPercent = (value) =>
    `${(Number(value) || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`;

const classifyAction = (action) => {
    const text = String(action || '').toLowerCase();
    if (text.includes('за счет озон') || text.includes('за счёт озон') || text.includes('ozon')) {
        return { label: 'Ozon', color: 'blue' };
    }
    if (text.includes('селлер') || text.includes('продав')) {
        return { label: 'Продавец', color: 'orange' };
    }
    if (text.includes('округл')) {
        return { label: 'Округление', color: 'default' };
    }
    if (text.includes('доставк')) {
        return { label: 'Доставка', color: 'cyan' };
    }
    return { label: 'Акция', color: 'purple' };
};

const priceBreakdown = (detail) => {
    const price = Number(detail.price) || 0;
    const sellerDiscount = Math.max(0, Number(detail.total_discount_value) || 0);
    const customer = Math.max(0, Number(detail.customer_price) || 0);
    const oldPrice = Number(detail.old_price) || 0;
    const base = Math.max(oldPrice > 0 ? oldPrice : price + sellerDiscount, price, customer, 1);
    const ozonPays = Math.max(0, price - customer);
    const ozonPercent = price > 0 ? (ozonPays / price) * 100 : 0;
    return {
        base,
        price,
        customer,
        sellerDiscount,
        ozonPays,
        ozonPercent,
        segments: [
            { key: 'customer', label: 'Платит покупатель', value: Math.min(customer, base) },
            { key: 'ozon', label: 'Компенсирует Ozon', value: ozonPays },
            { key: 'seller', label: 'Скидка продавца', value: sellerDiscount },
        ].filter((segment) => segment.value > 0),
    };
};

const findOrderProduct = (products, detail) =>
    (products || []).find((product) => String(product.sku) === String(detail.product_id)) || {};

const PriceDetailCard = ({ detail, product }) => {
    const currency = detail.currency || 'RUB';
    const breakdown = priceBreakdown(detail);
    const title = product.color?.shortName || product.name || product.offer_id || `SKU ${detail.product_id}`;
    const image = product.primary_image || product.image || product.color?.image;

    return (
        <section className="ozon-price-card">
            <header className="ozon-price-card__header">
                {image ? <img src={image} alt="" className="ozon-price-card__image" /> : null}
                <div className="ozon-price-card__title">
                    <div className="ozon-price-card__name">{title}</div>
                    <div className="ozon-price-card__meta">
                        {product.offer_id || '—'} · SKU {detail.product_id || '—'}
                        {detail.quantity > 1 ? ` · ${detail.quantity} шт` : ''}
                    </div>
                </div>
            </header>

            <div className="ozon-price-card__stats">
                <div className="ozon-price-stat ozon-price-stat--old">
                    <span className="ozon-price-stat__label">Зачеркнутая цена</span>
                    <span className="ozon-price-stat__value">
                        {Number(detail.old_price) > 0 ? formatMoney(detail.old_price, currency) : '—'}
                    </span>
                </div>
                <span className="ozon-price-stat-operator" aria-hidden="true">−</span>
                <div className="ozon-price-stat ozon-price-stat--seller">
                    <span className="ozon-price-stat__label">Скидка продавца</span>
                    <span className="ozon-price-stat__value">
                        {formatMoney(detail.total_discount_value, currency)}
                    </span>
                    <span className="ozon-price-stat__hint">
                        {formatPercent(detail.total_discount_percent)}
                    </span>
                </div>
                <span className="ozon-price-stat-operator" aria-hidden="true">=</span>
                <div className="ozon-price-stat">
                    <span className="ozon-price-stat__label">Цена продавца</span>
                    <span className="ozon-price-stat__value">{formatMoney(detail.price, currency)}</span>
                </div>
                <span className="ozon-price-stat-operator" aria-hidden="true">−</span>
                <div className="ozon-price-stat ozon-price-stat--ozon">
                    <span className="ozon-price-stat__label">Компенсирует Ozon</span>
                    <span className="ozon-price-stat__value">
                        {formatMoney(breakdown.ozonPays, currency)}
                    </span>
                    <span className="ozon-price-stat__hint">
                        {formatPercent(breakdown.ozonPercent)} от цены
                    </span>
                </div>
                <span className="ozon-price-stat-operator" aria-hidden="true">=</span>
                <div className="ozon-price-stat ozon-price-stat--customer">
                    <span className="ozon-price-stat__label">Платит покупатель</span>
                    <span className="ozon-price-stat__value">
                        {formatMoney(detail.customer_price, currency)}
                    </span>
                </div>
            </div>

            <div className="ozon-price-bar" aria-label="Структура цены">
                {breakdown.segments.map((segment) => (
                    <Tooltip
                        key={segment.key}
                        title={`${segment.label}: ${formatMoney(segment.value, currency)}`}
                    >
                        <div
                            className={`ozon-price-bar__segment ozon-price-bar__segment--${segment.key}`}
                            style={{ width: `${(segment.value / breakdown.base) * 100}%` }}
                        />
                    </Tooltip>
                ))}
            </div>
            <div className="ozon-price-bar__scale">
                <span>0</span>
                <span>Цена продавца {formatMoney(breakdown.price, currency)}</span>
                <span>{formatMoney(breakdown.base, currency)}</span>
            </div>
            <div className="ozon-price-legend">
                {breakdown.segments.map((segment) => (
                    <span key={segment.key} className="ozon-price-legend__item">
                        <i className={`ozon-price-legend__dot ozon-price-bar__segment--${segment.key}`} />
                        {segment.label}
                    </span>
                ))}
            </div>

            <div className="ozon-price-actions">
                <div className="ozon-price-actions__title">
                    Акции и скидки ({detail.actions?.length || 0})
                </div>
                {detail.actions?.length ? (
                    <ul className="ozon-price-actions__list">
                        {detail.actions.map((action, index) => {
                            const kind = classifyAction(action);
                            return (
                                <li key={`${action}-${index}`} className="ozon-price-actions__item">
                                    <Tag color={kind.color}>{kind.label}</Tag>
                                    <span>{action}</span>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <div className="ozon-price-actions__empty">Акций нет</div>
                )}
            </div>
        </section>
    );
};

const OzonPriceDetailsModal = ({ order, onClose }) => {
    const details = order?.financial_products || [];
    const postingNumber = String(order?.posting_number || '').replace(/\s/g, '');
    const [rawOpen, setRawOpen] = useState(false);
    const [raw, setRaw] = useState({ loading: false, data: null, error: null });

    const latestPosting = useRef(postingNumber);

    useEffect(() => {
        latestPosting.current = postingNumber;
        setRawOpen(false);
        setRaw({ loading: false, data: null, error: null });
    }, [postingNumber]);

    const toggleRaw = () => {
        const nextOpen = !rawOpen;
        setRawOpen(nextOpen);
        if (!nextOpen || !postingNumber || raw.data || raw.loading) return;
        const requestedFor = postingNumber;
        setRaw({ loading: true, data: null, error: null });
        axios.get(`${API_URL}/v1/orders/ozon/posting-raw`, {
            params: { PostingNumber: requestedFor },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        })
            .then((response) => ({ loading: false, data: response.data, error: null }))
            .catch((error) => ({
                loading: false,
                data: null,
                error: String(error.response?.data?.detail || error.message),
            }))
            .then((next) => {
                if (latestPosting.current === requestedFor) setRaw(next);
            });
    };

    return (
        <Modal
            open={Boolean(order)}
            onCancel={onClose}
            footer={null}
            width={820}
            title={(
                <div className="ozon-price-details__title">
                    <span>{`Цена в заказе ${postingNumber}`}</span>
                    <Tooltip title={rawOpen ? 'Скрыть JSON заказа' : 'Показать сырой JSON заказа'}>
                        <Button
                            type="text"
                            size="small"
                            icon={<QuestionCircleOutlined />}
                            className={rawOpen ? 'ozon-price-details__raw-toggle--active' : undefined}
                            onClick={toggleRaw}
                        />
                    </Tooltip>
                </div>
            )}
            destroyOnClose
        >
            {details.length ? (
                <div className="ozon-price-details">
                    {details.map((detail, index) => (
                        <PriceDetailCard
                            key={`${detail.product_id}-${index}`}
                            detail={detail}
                            product={findOrderProduct(order.products, detail)}
                        />
                    ))}
                </div>
            ) : (
                <Empty description="Ozon не передал финансовые данные по товарам. Обновите заказы." />
            )}
            {rawOpen ? (
                <div className="ozon-price-raw">
                    <div className="ozon-price-raw__title">Сырой JSON заказа (Ozon /v3/posting/fbs/get)</div>
                    {raw.loading ? <Spin /> : null}
                    {raw.error ? <Alert type="error" showIcon message={raw.error} /> : null}
                    {raw.data ? (
                        <pre className="ozon-price-raw__json">{JSON.stringify(raw.data, null, 2)}</pre>
                    ) : null}
                </div>
            ) : null}
        </Modal>
    );
};

export default OzonPriceDetailsModal;
